import { makeAutoObservable } from "mobx";
import type { InjectionToken } from "tsyringe";

export type RealtimeConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "offline"
  | "error"
  | "disabled";

export interface RealtimeServerEvent<TPayload = Record<string, unknown>> {
  id: string;
  type: string;
  topic: string;
  timestamp: string;
  payload: TPayload;
}

interface RealtimeEnvelope {
  success: boolean;
  event?: RealtimeServerEvent;
  error?: string;
}

export interface RealtimeFeedItem {
  id: string;
  type: string;
  topic: string;
  timestamp: string;
  title: string;
  description: string;
  tone: "info" | "warning";
  sourceType: "user" | "system";
  entity?: string;
  operation?: "created" | "updated" | "deleted";
  payloadPath?: string;
  productId?: string;
  boxId?: string;
}

export interface IRealtimeStore {
  status: RealtimeConnectionStatus;
  events: RealtimeFeedItem[];
  statusLabel: string;
  lastError: string | null;
  isNetworkOnline: boolean;
  reconnectAttemptsTotal: number;
  reconnectStormCount: number;
  canReconnect: boolean;
  reconnectCooldownSeconds: number;
  start: () => void;
  stop: () => void;
  reconnect: () => void;
}

export const REALTIME_STORE_TOKEN = Symbol(
  "IRealtimeStore",
) as InjectionToken<IRealtimeStore>;

const MAX_EVENTS = 30;
const TOKEN_STORAGE_KEYS = ["accessToken", "access_token", "token"];
const AUTH_STORAGE_KEYS = ["auth", "authTokens", "warehouseAuth"];
const DEFAULT_TOPICS = ["entity.*", "product.expiring", "ws.system", "all"];
const RECONNECT_BASE_DELAY_MS = 700;
const RECONNECT_MAX_DELAY_MS = 30_000;
const RECONNECT_ERROR_VISIBLE_AFTER_ATTEMPTS = 3;
const MANUAL_RECONNECT_COOLDOWN_MS = 2_000;
const RECONNECT_STORM_WINDOW_MS = 20_000;
const RECONNECT_STORM_ATTEMPTS = 6;
const RECONNECT_STORM_COOLDOWN_MS = 15_000;

function normalizeWsPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return "/ws";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function getWsEndpointUrl(): URL | null {
  if (typeof window === "undefined") {
    return null;
  }

  const explicitUrl = String(import.meta.env.VITE_WS_URL || "").trim();
  if (explicitUrl) {
    return new URL(explicitUrl, window.location.href);
  }

  const wsPath = normalizeWsPath(String(import.meta.env.VITE_WS_PATH || "/ws"));
  const apiBaseUrl = String(
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "",
  ).trim();

  if (apiBaseUrl) {
    const apiUrl = new URL(apiBaseUrl, window.location.href);
    const protocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
    return new URL(`${protocol}//${apiUrl.host}${wsPath}`);
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return new URL(`${protocol}//${window.location.host}${wsPath}`);
}

function readTokenFromAuthObject(rawAuth: string | null): string | null {
  if (!rawAuth) return null;

  try {
    const parsed = JSON.parse(rawAuth) as Record<string, unknown>;
    const token =
      parsed.accessToken || parsed.access_token || parsed.token || null;
    return typeof token === "string" && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

function getRealtimeToken(): string | null {
  const envToken = String(import.meta.env.VITE_WS_ACCESS_TOKEN || "").trim();
  if (envToken) {
    return envToken;
  }

  if (typeof window === "undefined") {
    return null;
  }

  for (const key of TOKEN_STORAGE_KEYS) {
    const value = window.localStorage.getItem(key);
    if (value?.trim()) {
      return value.trim();
    }
  }

  for (const key of AUTH_STORAGE_KEYS) {
    const token = readTokenFromAuthObject(window.localStorage.getItem(key));
    if (token) {
      return token;
    }
  }

  return null;
}

function buildWsUrl(token: string, clientId: string): string | null {
  const endpointUrl = getWsEndpointUrl();
  if (!endpointUrl) return null;

  endpointUrl.searchParams.set("access_token", token);
  endpointUrl.searchParams.set("clientId", clientId);
  return endpointUrl.toString();
}

function titleCaseEntity(entity: string): string {
  if (entity === "products") return "Продукты";
  if (entity === "boxes") return "Коробки";
  if (entity === "journals") return "Журнал";
  if (entity === "users") return "Пользователи";
  if (entity === "sync") return "Синхронизация";
  return entity;
}

function resolveMutationTitle(
  entity: string,
  operation: string,
  bulk: boolean,
): string {
  if (entity === "products") {
    if (operation === "created") {
      return bulk ? "Товары добавлены" : "Товар добавлен";
    }
    if (operation === "updated") {
      return bulk ? "Товары обновлены" : "Товар обновлен";
    }
    if (operation === "deleted") {
      return bulk ? "Товары удалены" : "Товар удален";
    }
  }

  if (entity === "boxes") {
    if (operation === "created") {
      return bulk ? "Коробки добавлены" : "Коробка добавлена";
    }
    if (operation === "updated") {
      return bulk ? "Коробки обновлены" : "Коробка обновлена";
    }
    if (operation === "deleted") {
      return bulk ? "Коробки удалены" : "Коробка удалена";
    }
  }

  if (operation === "created") {
    return bulk ? "Сущности созданы" : "Сущность создана";
  }
  if (operation === "updated") {
    return bulk ? "Сущности обновлены" : "Сущность обновлена";
  }
  if (operation === "deleted") {
    return bulk ? "Сущности удалены" : "Сущность удалена";
  }
  return "Изменение данных";
}

function createFeedItem(event: RealtimeServerEvent): RealtimeFeedItem {
  const payload = event.payload as Record<string, unknown>;

  if (event.type === "product.expiring") {
    const name = String(payload.name || "Продукт");
    const daysLeft = Number(payload.daysLeft || 0);
    const productId = String(payload.productId || "").trim();
    return {
      id: event.id,
      type: event.type,
      topic: event.topic,
      timestamp: event.timestamp,
      title: "Срок годности истекает",
      description:
        daysLeft > 0
          ? `${name}: осталось ${daysLeft} дн.`
          : `${name}: срок истекает сегодня`,
      tone: "warning",
      sourceType: "system",
      productId: productId || undefined,
    };
  }

  if (event.type.startsWith("entity.")) {
    const operation = String(payload.operation || "").trim();
    const entityId = String(payload.entity || "").trim();
    const entityLabel = titleCaseEntity(entityId || "entity");
    const bulk = Boolean(payload.bulk);
    const actorUserId = String(payload.actorUserId || "").trim();
    const actorLabel = String(payload.actorLabel || "").trim();
    const entityName = String(payload.entityName || "").trim();
    const mutationEntityId = String(payload.entityId || "").trim();
    const productId = String(payload.productId || "").trim();
    const boxId = String(payload.boxId || "").trim();
    const parentId = String(payload.parentId || "").trim();
    const quantity = Number(payload.quantity);
    const bulkCount = Number(payload.bulkCount);
    const payloadPath =
      typeof payload.path === "string" && payload.path.trim()
        ? payload.path.trim()
        : undefined;
    const descriptionLines: string[] = [];

    if (entityName) {
      if (entityId === "products") {
        descriptionLines.push(`Товар: ${entityName}`);
      } else if (entityId === "boxes") {
        descriptionLines.push(`Коробка: ${entityName}`);
      } else {
        descriptionLines.push(`Объект: ${entityName}`);
      }
    }
    if (!entityName && mutationEntityId) {
      descriptionLines.push(`ID: ${mutationEntityId}`);
    }

    if (Number.isFinite(quantity)) {
      descriptionLines.push(`Количество: ${quantity}`);
    }

    if (parentId) {
      descriptionLines.push(`Коробка: ${parentId}`);
    } else if (entityId === "products" && boxId) {
      descriptionLines.push(`Коробка: ${boxId}`);
    }

    if (Number.isFinite(bulkCount) && bulkCount > 0) {
      descriptionLines.push(`Элементов в операции: ${bulkCount}`);
    }

    if (entityId === "products" && operation === "created") {
      descriptionLines.push("Комментарий: Создание продукта");
    }

    descriptionLines.push(
      `Источник: ${
        actorUserId ? "действие пользователя" : "системное событие"
      }`,
    );

    if (actorLabel) {
      descriptionLines.push(`Автор: ${actorLabel}`);
    } else if (actorUserId) {
      descriptionLines.push(`Автор: ${actorUserId}`);
    }

    if (descriptionLines.length === 0) {
      descriptionLines.push(
        `${entityLabel} ${operation === "created" ? "создан(а)" : operation === "updated" ? "обновлен(а)" : operation === "deleted" ? "удален(а)" : "изменен(а)"}`,
      );
    }

    return {
      id: event.id,
      type: event.type,
      topic: event.topic,
      timestamp: event.timestamp,
      title: resolveMutationTitle(entityId, operation, bulk),
      description: descriptionLines.join("\n"),
      tone: operation === "deleted" ? "warning" : "info",
      sourceType: actorUserId ? "user" : "system",
      entity: entityId || undefined,
      operation:
        operation === "created" ||
        operation === "updated" ||
        operation === "deleted"
          ? operation
          : undefined,
      payloadPath,
      productId: productId || undefined,
      boxId: boxId || undefined,
    };
  }

  if (event.type === "ws.connected") {
    return {
      id: event.id,
      type: event.type,
      topic: event.topic,
      timestamp: event.timestamp,
      title: "WebSocket подключен",
      description: "Канал realtime активен",
      tone: "info",
      sourceType: "system",
    };
  }

  return {
    id: event.id,
    type: event.type,
    topic: event.topic,
    timestamp: event.timestamp,
    title: event.type,
    description: `Топик: ${event.topic}`,
    tone: "info",
    sourceType: "system",
  };
}

export class RealtimeStore implements IRealtimeStore {
  status: RealtimeConnectionStatus = "idle";

  lastError: string | null = null;

  isNetworkOnline = true;

  reconnectAttemptsTotal = 0;

  reconnectStormCount = 0;

  events: RealtimeFeedItem[] = [];

  reconnectCooldownSeconds = 0;

  private socket: WebSocket | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private pingTimer: ReturnType<typeof setInterval> | null = null;

  private reconnectCooldownTimer: ReturnType<typeof setInterval> | null = null;

  private reconnectAttempt = 0;

  private manuallyStopped = true;

  private reconnectAttemptTimestamps: number[] = [];

  private reconnectSuspendedUntil = 0;

  private manualReconnectLockedUntil = 0;

  private readonly clientId = `web_${crypto.randomUUID()}`;

  constructor() {
    this.syncNetworkStateFromNavigator();
    this.setupNetworkListeners();
    makeAutoObservable(this);
  }

  get isConnected(): boolean {
    return this.status === "connected";
  }

  get canReconnect(): boolean {
    if (this.status === "disabled") {
      return false;
    }

    if (!this.isNetworkOnline) {
      return false;
    }

    if (this.reconnectTimer) {
      return false;
    }

    if (
      this.status === "connecting" &&
      this.socket &&
      this.socket.readyState === WebSocket.CONNECTING
    ) {
      return false;
    }

    return this.reconnectCooldownSeconds <= 0;
  }

  get statusLabel(): string {
    if (this.status === "connected") return "realtime: в сети";
    if (this.status === "connecting") return "realtime: подключаемся";
    if (this.status === "reconnecting") {
      if (this.reconnectCooldownSeconds > 0) {
        return `realtime: пауза ${this.reconnectCooldownSeconds}с`;
      }
      return "realtime: переподключаемся";
    }
    if (this.status === "offline") return "realtime: нет сети";
    if (this.status === "disabled") return "realtime: токен не найден";
    if (this.status === "error") return "realtime: ошибка канала";
    return "realtime: отключен";
  }

  start(): void {
    this.manuallyStopped = false;
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return;
    }
    if (this.reconnectTimer) {
      return;
    }
    this.connect(false);
  }

  stop(): void {
    this.manuallyStopped = true;
    this.reconnectAttempt = 0;
    this.reconnectAttemptTimestamps = [];
    this.reconnectSuspendedUntil = 0;
    this.manualReconnectLockedUntil = 0;
    this.clearReconnectTimer();
    this.clearReconnectCooldownTimer();
    this.clearPingTimer();
    const activeSocket = this.socket;
    this.socket = null;
    this.disconnectSocket(activeSocket, "manual stop");
    this.setStatus("idle");
    this.setError(null);
  }

  reconnect(): void {
    const now = Date.now();
    this.manuallyStopped = false;
    if (!this.isNetworkOnline) {
      this.setStatus("offline");
      this.setError(
        "Нет подключения к сети. Realtime возобновится при восстановлении интернета.",
      );
      return;
    }

    if (now < this.manualReconnectLockedUntil) {
      const seconds = Math.max(
        1,
        Math.ceil((this.manualReconnectLockedUntil - now) / 1000),
      );
      this.setStatus("reconnecting");
      this.setError(
        `Слишком частый reconnect. Повторите попытку через ${seconds} сек.`,
      );
      this.updateReconnectCooldownSeconds();
      return;
    }

    this.manualReconnectLockedUntil = now + MANUAL_RECONNECT_COOLDOWN_MS;
    this.updateReconnectCooldownSeconds();
    this.reconnectAttempt = 0;
    this.reconnectAttemptTimestamps = [];
    this.reconnectSuspendedUntil = 0;
    this.clearReconnectTimer();
    this.clearReconnectCooldownTimer();
    this.clearPingTimer();

    const activeSocket = this.socket;
    this.socket = null;
    this.disconnectSocket(activeSocket, "manual reconnect");

    this.manuallyStopped = false;
    this.connect(false);
  }

  clearEvents(): void {
    this.events = [];
  }

  private connect(isRetry: boolean): void {
    if (this.manuallyStopped) {
      return;
    }

    if (!this.isNetworkOnline) {
      this.setStatus("offline");
      this.setError(
        "Нет подключения к сети. Realtime возобновится при восстановлении интернета.",
      );
      return;
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    const token = getRealtimeToken();
    if (!token) {
      this.setStatus("disabled");
      this.setError(
        "JWT токен не найден. Сохраните access token в localStorage (ключ accessToken).",
      );
      return;
    }

    const wsUrl = buildWsUrl(token, this.clientId);
    if (!wsUrl) {
      this.setStatus("error");
      this.setError("Не удалось сформировать URL для WebSocket подключения.");
      return;
    }

    this.setStatus(isRetry ? "reconnecting" : "connecting");
    if (!isRetry) {
      this.setError(null);
    }

    try {
      const socket = new WebSocket(wsUrl);
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket) {
          return;
        }

        this.reconnectAttempt = 0;
        this.reconnectAttemptTimestamps = [];
        this.reconnectSuspendedUntil = 0;
        this.setStatus("connected");
        this.setError(null);
        this.sendCommand({ action: "subscribe", topics: DEFAULT_TOPICS });
        this.startPing();
      };

      socket.onmessage = (messageEvent) => {
        if (this.socket !== socket) {
          return;
        }
        this.handleMessage(messageEvent.data);
      };

      socket.onerror = () => {
        if (this.socket !== socket) {
          return;
        }
        // onerror в браузере часто не содержит полезных деталей.
        // Ошибку показываем только если переподключение долго не удается.
        if (this.reconnectAttempt >= RECONNECT_ERROR_VISIBLE_AFTER_ATTEMPTS) {
          this.setError(
            "Проблемы с realtime-каналом. Пытаемся восстановить соединение.",
          );
        }
      };

      socket.onclose = () => {
        if (this.socket !== socket) {
          return;
        }

        this.socket = null;
        this.clearPingTimer();
        if (this.manuallyStopped) {
          this.setStatus("idle");
          return;
        }
        this.scheduleReconnect();
      };
    } catch {
      this.setStatus("error");
      this.setError("Не удалось открыть WebSocket подключение.");
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.manuallyStopped || this.reconnectTimer) {
      return;
    }

    if (!this.isNetworkOnline) {
      this.setStatus("offline");
      this.updateReconnectCooldownSeconds();
      this.setError(
        "Нет подключения к сети. Realtime возобновится при восстановлении интернета.",
      );
      return;
    }

    const now = Date.now();
    if (this.reconnectSuspendedUntil > now) {
      const delayMs = this.reconnectSuspendedUntil - now;
      this.updateReconnectCooldownSeconds();
      this.setStatus("reconnecting");
      this.setError(
        `Realtime временно нестабилен. Следующая попытка через ${Math.max(
          1,
          Math.ceil(delayMs / 1000),
        )} сек.`,
      );
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect(true);
      }, delayMs);
      return;
    }

    this.reconnectAttempt += 1;
    this.reconnectAttemptsTotal += 1;
    this.rememberReconnectAttempt(now);

    let backoffMs = this.getReconnectDelayMs(this.reconnectAttempt);

    if (this.isReconnectStorm()) {
      this.reconnectStormCount += 1;
      this.reconnectSuspendedUntil = now + RECONNECT_STORM_COOLDOWN_MS;
      backoffMs = Math.max(backoffMs, RECONNECT_STORM_COOLDOWN_MS);
      this.updateReconnectCooldownSeconds();
      this.setError(
        `Слишком много переподключений. Пауза ${Math.max(
          1,
          Math.ceil(backoffMs / 1000),
        )} сек.`,
      );
    } else if (
      this.reconnectAttempt >= RECONNECT_ERROR_VISIBLE_AFTER_ATTEMPTS
    ) {
      this.setError(
        "Проблемы с realtime-каналом. Пытаемся восстановить соединение.",
      );
    }

    this.setStatus("reconnecting");

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(true);
    }, backoffMs);
  }

  private getReconnectDelayMs(attempt: number): number {
    const boundedAttempt = Math.max(1, attempt);
    const exponentialDelay = Math.min(
      RECONNECT_MAX_DELAY_MS,
      RECONNECT_BASE_DELAY_MS * 2 ** (boundedAttempt - 1),
    );
    const jitterMax = Math.max(120, Math.floor(exponentialDelay * 0.25));
    const jitter = Math.floor(Math.random() * jitterMax);
    return exponentialDelay + jitter;
  }

  private rememberReconnectAttempt(timestamp: number): void {
    this.reconnectAttemptTimestamps = [
      ...this.reconnectAttemptTimestamps.filter(
        (item) => timestamp - item < RECONNECT_STORM_WINDOW_MS,
      ),
      timestamp,
    ];
  }

  private isReconnectStorm(): boolean {
    return this.reconnectAttemptTimestamps.length >= RECONNECT_STORM_ATTEMPTS;
  }

  private updateReconnectCooldownSeconds(): void {
    const now = Date.now();
    if (!this.isNetworkOnline) {
      this.reconnectCooldownSeconds = 0;
      this.clearReconnectCooldownTimer();
      return;
    }

    const reconnectBlockedUntil = Math.max(
      this.manualReconnectLockedUntil,
      this.reconnectSuspendedUntil,
    );
    const seconds =
      reconnectBlockedUntil > now
        ? Math.max(1, Math.ceil((reconnectBlockedUntil - now) / 1000))
        : 0;

    this.reconnectCooldownSeconds = seconds;

    if (seconds > 0) {
      this.ensureReconnectCooldownTimer();
      return;
    }

    this.clearReconnectCooldownTimer();
  }

  private ensureReconnectCooldownTimer(): void {
    if (!this.isNetworkOnline) {
      return;
    }

    if (this.reconnectCooldownTimer) {
      return;
    }

    this.reconnectCooldownTimer = setInterval(() => {
      this.updateReconnectCooldownSeconds();
    }, 200);
  }

  private clearReconnectCooldownTimer(): void {
    if (!this.reconnectCooldownTimer) return;
    clearInterval(this.reconnectCooldownTimer);
    this.reconnectCooldownTimer = null;
    this.reconnectCooldownSeconds = 0;
  }

  private disconnectSocket(socket: WebSocket | null, reason: string): void {
    if (!socket) {
      return;
    }

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      try {
        socket.close(1000, reason);
      } catch {
        // ignore close errors on stale sockets
      }
    }
  }

  private startPing(): void {
    this.clearPingTimer();
    this.pingTimer = setInterval(() => {
      this.sendCommand({ action: "ping" });
    }, 25_000);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearPingTimer(): void {
    if (!this.pingTimer) return;
    clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private sendCommand(command: unknown): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify(command));
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== "string") {
      return;
    }

    let envelope: RealtimeEnvelope;
    try {
      envelope = JSON.parse(raw) as RealtimeEnvelope;
    } catch {
      return;
    }

    if (!envelope.success) {
      this.setError(envelope.error || "Ошибка обработки realtime-сообщения.");
      return;
    }

    if (!envelope.event) {
      return;
    }

    this.pushEvent(createFeedItem(envelope.event));
  }

  private setStatus(status: RealtimeConnectionStatus): void {
    this.status = status;
  }

  private setError(error: string | null): void {
    this.lastError = error;
  }

  private pushEvent(event: RealtimeFeedItem): void {
    this.events = [event, ...this.events].slice(0, MAX_EVENTS);
  }

  private setupNetworkListeners(): void {
    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("online", this.handleNetworkOnline);
    window.addEventListener("offline", this.handleNetworkOffline);
  }

  private syncNetworkStateFromNavigator(): void {
    if (typeof navigator === "undefined") {
      this.isNetworkOnline = true;
      return;
    }

    const onlineState = navigator.onLine;
    this.isNetworkOnline =
      typeof onlineState === "boolean" ? onlineState : true;
  }

  private readonly handleNetworkOnline = (): void => {
    this.isNetworkOnline = true;
    this.setError(null);

    if (this.manuallyStopped) {
      if (this.status === "offline") {
        this.setStatus("idle");
      }
      return;
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    this.clearReconnectTimer();
    this.connect(true);
  };

  private readonly handleNetworkOffline = (): void => {
    this.isNetworkOnline = false;
    this.clearReconnectTimer();
    this.clearReconnectCooldownTimer();
    this.clearPingTimer();

    const activeSocket = this.socket;
    this.socket = null;
    this.disconnectSocket(activeSocket, "network offline");

    if (this.manuallyStopped) {
      this.setStatus("idle");
      return;
    }

    this.setStatus("offline");
    this.setError(
      "Нет подключения к сети. Realtime возобновится при восстановлении интернета.",
    );
  };
}
